import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, RefreshCw, Loader2, FileDown, TrendingUp } from 'lucide-react';
import ConsistencyBadge from '../../components/ConsistencyBadge';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  AreaChart, Area,
} from 'recharts';
import { isBrandingOnlyBrand } from '../../utils/brandingClassifier';
// Branding raporundan düşen kampanyalar — faturanın hariç listesinden yalnızca
// `raporlardanCikar` işaretliler (Yataş Bedding: Yunanistan). Dormy / Urban Mood KALIR.
import { haricleriAyikla } from '../../utils/haricKampanyalar';
import { useMetaReach, mrNew, mrAddRow, sumMetrics, withDedupReach } from '../../utils/metaReachDedup';

/* ── Constants ──────────────────────────────────────────────────────── */

const API = 'https://api.glowytics.com';

const BRAND_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'];

/* Columns shown in the detail (Planlamadaki Kırılımlar) table */
const COLUMNS = [
  { key: 'harcama',  label: 'Harcama',  format: 'currency' },
  { key: 'spendPct', label: '% Harcama', format: 'percent',
    compute: (m, ctx) => {
      const total = ctx && ctx.totalHarcama;
      if (!m || !total || total <= 0) return null;
      return (Number(m.harcama) || 0) / total;
    } },
  { key: 'gosterim', label: 'Gösterim', format: 'number'   },
  { key: 'erisim',   label: 'Erişim',   format: 'number'   },
  { key: 'izlenme',  label: 'İzlenme',  format: 'number'   },
  { key: 'tiklama',  label: 'Tıklama',  format: 'number'   },
  { key: 'trafik',   label: 'Trafik',   format: 'number'   },
  { key: 'cpm', label: 'CPM', format: 'currencyFine',
    compute: (m) => (m && m.gosterim > 0 ? (Number(m.harcama) || 0) / m.gosterim * 1000 : null) },
  { key: 'cpc', label: 'CPC', format: 'currencyFine',
    compute: (m) => (m && m.tiklama > 0 ? (Number(m.harcama) || 0) / m.tiklama : null) },
  { key: 'cpv', label: 'CPV', format: 'currencyFine',
    compute: (m) => (m && m.izlenme > 0 ? (Number(m.harcama) || 0) / m.izlenme : null) },
];

/* Helper for rendering either derived or direct metric values */
const getCellValue = (metrics, col, context) => {
  if (!metrics) return null;
  if (col.compute) return col.compute(metrics, context);
  return metrics[col.key];
};

/* Branding campaign type rows per platform (drawn from the paper sketch + Report 2 classifier) */
const BRANDING_TABLE = [
  {
    platform: 'Google',
    rows: [
      { name: 'YouTube' },
      { name: 'Demand Gen' },
      { name: 'Display / GDN' },
      { name: 'P-Max Branding' },
    ],
  },
  {
    platform: 'Meta',
    rows: [
      { name: 'Reach / Awareness' },
      { name: 'Engagement' },
      { name: 'ThruPlay / Video Views' },
    ],
  },
  {
    platform: 'Pinterest',
    rows: [{ name: 'Pinterest / Branding' }],
  },
  {
    platform: 'TikTok',
    rows: [{ name: 'TikTok / Branding (Manuel)' }],
  },
  {
    platform: 'Medya Planı',
    rows: [{ name: 'Native' }, { name: 'Display' }, { name: 'Video' }],
  },
];

/* Channel-level summary rows (Mecra Kırılımları / Branding) */
const SUMMARY_ROWS = [
  { label: 'Google',      sources: [{ platform: 'Google', rows: ['YouTube', 'Demand Gen', 'Display / GDN', 'P-Max Branding'] }] },
  { label: 'Meta',        sources: [{ platform: 'Meta', rows: ['Reach / Awareness', 'Engagement', 'ThruPlay / Video Views'] }] },
  { label: 'Pinterest',   sources: [{ platform: 'Pinterest', rows: ['Pinterest / Branding'] }] },
  { label: 'TikTok',      sources: [{ platform: 'TikTok', rows: ['TikTok / Branding (Manuel)'] }] },
  { label: 'Medya Planı', sources: [{ platform: 'Medya Planı', rows: ['Native', 'Display', 'Video'] }] },
];

const EXCLUDE_FROM_MANUAL_BRANDING = [
  'google_ads', 'meta_ads', 'google_ads_performance', 'meta_ads_performance',
  'google_ads_branding', 'meta_ads_branding',
  'analytics_revenue', 'analytics_revenue_performance', 'analytics_revenue_branding',
];

const DATE_PRESETS = [
  { key: 'last7',   label: 'Son 7 Gün'    },
  { key: 'last30',  label: 'Son 30 Gün'   },
  { key: 'lastMonth', label: 'Geçen Ay'  },
  { key: 'thisMonth', label: 'Bu Ay'     },
  { key: 'last90',  label: 'Son 90 Gün'  },
  { key: 'custom',  label: 'Özel'        },
];

/* ── Helpers ────────────────────────────────────────────────────────── */

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getLastCompletedMonthRange = () => {
  const now = new Date();
  return {
    startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
    endDate: new Date(now.getFullYear(), now.getMonth(), 0),
  };
};

const resolvePreset = (key) => {
  const today = new Date();
  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  switch (key) {
    case 'last7':  start.setDate(start.getDate() - 6);  return { startDate: start, endDate: end };
    case 'last30': start.setDate(start.getDate() - 29); return { startDate: start, endDate: end };
    case 'last90': start.setDate(start.getDate() - 89); return { startDate: start, endDate: end };
    case 'thisMonth': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: s, endDate: end };
    }
    case 'lastMonth': return getLastCompletedMonthRange();
    default: return null;
  }
};

const daysOverlap = (startA, endA, startB, endB) => {
  const start = startA > startB ? startA : startB;
  const end = endA < endB ? endA : endB;
  if (end < start) return 0;
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
};

const listYearMonthInRange = (startDate, endDate) => {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const periods = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    periods.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
};

const normalizeLabel = (v) => (v || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '' || value === 0) return '';
  switch (format) {
    case 'currency':     return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    case 'currencyFine': return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    case 'number':       return new Intl.NumberFormat('tr-TR').format(Math.round(value));
    case 'percent':      return `%${(value * 100).toFixed(1)}`;
    default:         return value;
  }
};

const formatShort = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('tr-TR').format(Math.round(n));
};

const hdr = (brandId) => ({
  'X-Brand-ID': brandId,
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

/* ── Branding classifiers (mirrors CustomReport2.js) ──────────────── */

const isBranding = (name, campaignType) => {
  const n = (name || '').toString().toLowerCase();
  const t = (campaignType || '').toString().toLowerCase();
  return (
    n.includes('branding') ||
    n.includes('demand gen') ||
    n.includes('display') ||
    n.includes('youtube') ||
    t.includes('demand gen') ||
    t.includes('display') ||
    t.includes('youtube')
  );
};

// brandingOnly: markanın TÜM harcaması branding sayılıyorsa (Divanev — e-ticaret yok)
// PMax adında "branding" aranmaz ve Search de kendi satırını alır; yoksa hiçbir kovaya
// düşmeyip rapordan sessizce kaybolurlardı.
const classifyGoogleBrandingRow = (name, type, brandingOnly = false) => {
  const n = (name || '').toLowerCase();
  const t = (type || '').toLowerCase();
  if (t.includes('video') || n.includes('youtube') || t.includes('youtube')) return 'YouTube';
  if (t.includes('demand_gen') || n.includes('demand gen')) return 'Demand Gen';
  if (t.includes('display') || n.includes('display')) return 'Display / GDN';
  if (t.includes('performance_max') || t === 'pmax') {
    return (brandingOnly || n.includes('branding')) ? 'P-Max Branding' : null;
  }
  if (brandingOnly) return 'Search / Diğer';
  return null;
};

const classifyMetaBrandingRow = (objective, campaignName, brandingOnly = false) => {
  const name = (campaignName || '').toLowerCase().replace(/[\s\-_]/g, '');
  const nameRaw = (campaignName || '').toLowerCase();
  if (name.includes('videoview') || name.includes('thruplay')) return 'ThruPlay / Video Views';
  if (nameRaw.includes('instagram gönderi') || nameRaw.includes('instagram post')) return 'Engagement';
  if (name.includes('postengagement') || (name.includes('engagement') && !name.includes('video'))) return 'Engagement';
  if (name.includes('reach') || name.includes('brandawareness')) return 'Reach / Awareness';
  const obj = (objective || '').toUpperCase().trim();
  if (['REACH', 'OUTCOME_AWARENESS', 'AWARENESS', 'BRAND_AWARENESS'].includes(obj)) return 'Reach / Awareness';
  if (['OUTCOME_ENGAGEMENT', 'ENGAGEMENT', 'POST_ENGAGEMENT'].includes(obj)) return 'Engagement';
  if (['VIDEO_VIEWS', 'THRUPLAY'].includes(obj)) return 'ThruPlay / Video Views';
  // Hepsi-branding markasında Traffic/Conversion kampanyaları da rapora girer.
  return brandingOnly ? 'Traffic / Conversion' : null;
};

/* ── Aggregators ───────────────────────────────────────────────────── */

function aggregateGoogleBranding(rawData, brandingOnly = false) {
  const empty = () => ({ harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0 });
  const buckets = {
    'YouTube':         empty(),
    'Demand Gen':      empty(),
    'Display / GDN':   empty(),
    'P-Max Branding':  empty(),
  };
  if (brandingOnly) buckets['Search / Diğer'] = empty();
  (rawData || []).forEach(r => {
    const name = r.campaignName || r.campaign_name || '';
    const type = r.campaignType || r.campaign_type;
    const bucket = classifyGoogleBrandingRow(name, type, brandingOnly);
    if (!bucket || !buckets[bucket]) return;
    buckets[bucket].harcama  += Number(r.googleAdsCost) || 0;
    buckets[bucket].gosterim += Number(r.googleAdsImpression) || 0;
    buckets[bucket].tiklama  += Number(r.googleAdsClicks) || 0;
    buckets[bucket].trafik   += Number(r.sessions) || 0;
  });
  return buckets;
}

function overlayYouTubeVideoData(googleBuckets, videoRows, brandingOnly = false) {
  // Endpoint 2026-07-28'den beri PMax / Demand Gen / Display izlenmelerini de
  // döndürüyor. Hepsi 'YouTube' kovasına yazılırsa PMax izlenmesi YouTube
  // etiketiyle görünür; kovalar (Demand Gen, Display / GDN, P-Max Branding) zaten
  // mevcut ve harcamaları orada duruyor → izlenmeyi kendi kovasına yazıyoruz.
  // Harcama/gösterim'e DOKUNULMUYOR (YouTube kovasındaki eski fallback hariç),
  // yoksa aggregateGoogleBranding ile çift sayılır.
  const perBucket = {};
  let ytViews = 0, ytImp = 0, ytCost = 0;

  (videoRows || []).forEach(r => {
    const type = String(r.campaign_type ?? r.campaignType ?? 'VIDEO').toUpperCase();
    const vv = Number(r.video_views) || 0;
    const imp = Number(r.impressions) || 0;
    // Bumper/non-skippable/CTV (view=0) → impression'ı izlenme say (backend effective_views).
    const ev = r.effective_views != null ? Number(r.effective_views) : (vv > 0 ? vv : imp);

    if (type === 'VIDEO') {
      ytViews += ev; ytImp += imp; ytCost += Number(r.cost) || 0;
      return;
    }
    const bucket = classifyGoogleBrandingRow(r.campaign_name || '', r.campaign_type, brandingOnly);
    if (!bucket || !googleBuckets[bucket]) return;   // branding olmayan → bu rapora ait değil
    perBucket[bucket] = (perBucket[bucket] || 0) + ev;
  });

  if (googleBuckets['YouTube']) {
    if (ytViews > 0) googleBuckets['YouTube'].izlenme = ytViews;
    if (ytImp > 0 && !googleBuckets['YouTube'].gosterim) googleBuckets['YouTube'].gosterim = ytImp;
    if (ytCost > 0 && !googleBuckets['YouTube'].harcama) googleBuckets['YouTube'].harcama = ytCost;
  }
  Object.entries(perBucket).forEach(([bucket, views]) => {
    if (views > 0) googleBuckets[bucket].izlenme = views;
  });
  return googleBuckets;
}

function aggregateMetaBranding(rawData, brandingOnly = false) {
  const empty = () => ({ harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0, _mr: mrNew() });
  const buckets = {
    'Reach / Awareness':       empty(),
    'Engagement':              empty(),
    'ThruPlay / Video Views':  empty(),
  };
  if (brandingOnly) buckets['Traffic / Conversion'] = empty();
  (rawData || []).forEach(r => {
    const objective = r.campaign_objective || r.campaignObjective;
    const cName = r.campaign_name || r.campaignName || '';
    const bucket = classifyMetaBrandingRow(objective, cName, brandingOnly);
    if (!bucket || !buckets[bucket]) return;
    buckets[bucket].harcama  += Number(r.spend) || 0;
    buckets[bucket].gosterim += Number(r.impressions) || 0;
    buckets[bucket].erisim   += Number(r.reach) || 0;
    // Erişim toplanamaz — kampanya kimliği tutulur, tekil değer Meta'dan sorulur
    mrAddRow(buckets[bucket]._mr, r, { level: 'campaign' });
    buckets[bucket].izlenme  += Number(r.view || r.trueplay || r.video_views || r.videoViews) || 0;
    buckets[bucket].tiklama  += Number(r.clicks) || 0;
    buckets[bucket].trafik   += Number(r.ga4_sessions || r.ga4Sessions) || 0;
  });
  return buckets;
}

function aggregatePinterestBranding(json) {
  const bucket = { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0 };
  (json?.campaigns || []).forEach(c => {
    const name = (c.name || '').toLowerCase();
    const isBrand = name.includes('brand') || name.includes('awareness') || name.includes('reach') || name.includes('traffic');
    if (!isBrand) return;
    bucket.harcama  += Number(c.spend) || 0;
    bucket.gosterim += Number(c.impressions) || 0;
    bucket.erisim   += Number(c.reach) || 0;
    bucket.izlenme  += Number(c.video_views) || 0;
    bucket.tiklama  += Number(c.clicks) || 0;
    // Google/Meta gibi trafik = GA4 oturumu. Backend bunu Pinterest×GA4 view'ından
    // döndürüyor; eskiden hiç yazılmadığı için Pinterest satırı hep 0 trafik gösteriyordu.
    bucket.trafik   += Number(c.ga4_sessions) || 0;
  });
  return { 'Pinterest / Branding': bucket };
}

function aggregateMediaBuyingBranding(json) {
  // Statik Native/Display/Video YERİNE girilen mecraları dinamik göster.
  // Tek KANONİK tablo seç (Dağılım → yoksa Mecra). Eskiden Mecra atlanıp yalnız Dağılım
  // işleniyordu → sadece-Mecra aylar (ör. EnzaHome Mayıs) BOŞ kalıyordu. Tek tablo
  // seçtiğimiz için çift sayım da olmaz (iki tablo aynı para).
  const channelBuckets = {};
  const tiktokBucket = { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0 };
  const parseTR = (str) => {
    if (!str || str === '-' || str === '–') return 0;
    const cleaned = String(str).replace(/[₺\s]/g, '').replace(/\./g, '').replace(/,/g, '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };
  const isTikTok = (ch) => (ch || '').toLowerCase().includes('tiktok') || (ch || '').toLowerCase().includes('tik tok');
  const isMecra = (t) => (t || '').toLowerCase().includes('mecra');
  const sections = json?.sections || [];
  const section =
    sections.find(s => s?.title === 'Medya Satın Alma Dağılımı') ||
    sections.find(s => isMecra(s?.title)) ||
    sections.find(s => Array.isArray(s?.rows) && s.rows.length) ||
    sections[0];
  (section?.rows || []).forEach(r => {
    if (isTikTok(r.channel)) {
      tiktokBucket.harcama  += parseTR(r.budget);
      tiktokBucket.gosterim += parseTR(r.impressions);
      tiktokBucket.erisim   += parseTR(r.reach);
      tiktokBucket.izlenme  += parseTR(r.views);
      tiktokBucket.tiklama  += parseTR(r.clicks);
      return;
    }
    const ch = (r.channel || '').trim();
    if (!ch) return;
    if (!channelBuckets[ch]) channelBuckets[ch] = { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0 };
    channelBuckets[ch].harcama  += parseTR(r.budget);
    channelBuckets[ch].gosterim += parseTR(r.impressions);
    channelBuckets[ch].erisim   += parseTR(r.reach);
    channelBuckets[ch].izlenme  += parseTR(r.views);
    channelBuckets[ch].tiklama  += parseTR(r.clicks);
  });
  return { mediaPlan: channelBuckets, tiktok: tiktokBucket };
}

/* ── Styles (mirrors Report 5) ─────────────────────────────────────── */

const S = {
  headerCell:   'px-2 py-[6px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-700 text-center',
  platformCell: 'px-2 py-[5px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-600',
  rowLabel:     'px-2 py-[5px] text-[10.5px] text-slate-700 font-medium whitespace-nowrap border border-slate-200 bg-white',
  dataCell:     'px-2 py-[5px] text-[10px] text-right tabular-nums text-slate-600 whitespace-nowrap border border-slate-200 bg-white',
  totalLabel:   'px-2 py-[5px] text-[10px] font-bold text-white uppercase tracking-wide whitespace-nowrap border border-slate-500 bg-slate-800',
  totalCell:    'px-2 py-[5px] text-[10px] font-bold text-right tabular-nums text-white whitespace-nowrap border border-slate-500 bg-slate-800',
};

/* ── KPI Card with sparkline + allocation % ────────────────────────── */

function KpiCard({ label, value, trend, color, allocation }) {
  const chartData = (trend || []).map((v, i) => ({ i, value: Number(v) || 0 }));
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm flex flex-col justify-between min-h-[110px]">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</div>
          <div className="text-[18px] font-bold text-slate-800 mt-0.5 tabular-nums">{value}</div>
        </div>
        <TrendingUp size={14} className="text-slate-300" />
      </div>
      {chartData.length > 1 && (
        <div style={{ width: '100%', height: 32 }} className="mt-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
              <defs>
                <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke={color} strokeWidth={1.5} fill={`url(#grad-${label})`} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {allocation && allocation.length > 0 && (
        <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1.5">
          {allocation.map((a, idx) => (
            <div key={a.label} className="flex items-center gap-1 text-[9px] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: BRAND_COLORS[idx % BRAND_COLORS.length] }} />
              <span className="font-medium text-slate-600">{a.label}</span>
              <span className="tabular-nums">%{a.pct.toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Inline pie (for summary table rows — campaign-type breakdown) ── */

function InlinePie({ data, size = 36 }) {
  const cleaned = (data || []).filter(d => (d.value || 0) > 0);
  if (cleaned.length === 0) return <div style={{ width: size, height: size }} />;
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={cleaned} dataKey="value" nameKey="name" innerRadius={size * 0.25} outerRadius={size * 0.48} paddingAngle={1} isAnimationActive={false}>
            {cleaned.map((entry, i) => <Cell key={entry.name} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
          </Pie>
          <Tooltip
            formatter={(val, name) => [new Intl.NumberFormat('tr-TR').format(Math.round(val)), name]}
            contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 4 }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ── Large distribution pie ───────────────────────────────────────── */

function DistributionPie({ title, data, format = 'number' }) {
  const total = (data || []).reduce((s, d) => s + (d.value || 0), 0);
  const cleaned = (data || []).filter(d => (d.value || 0) > 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
      <div className="text-[11px] font-semibold text-slate-700 mb-2">{title}</div>
      {cleaned.length === 0 ? (
        <div className="text-[10px] text-slate-400 py-8 text-center">Veri yok</div>
      ) : (
        <>
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={cleaned} dataKey="value" nameKey="name" innerRadius={40} outerRadius={68} paddingAngle={1.5} isAnimationActive={false}>
                  {cleaned.map((entry, i) => <Cell key={entry.name} fill={BRAND_COLORS[i % BRAND_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(val) => formatValue(val, format)} contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 4 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1">
            {cleaned.map((d, i) => (
              <div key={d.name} className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-1.5 truncate">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BRAND_COLORS[i % BRAND_COLORS.length] }} />
                  <span className="text-slate-600 truncate">{d.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-slate-500 tabular-nums">{formatValue(d.value, format)}</span>
                  <span className="text-slate-400 tabular-nums w-10 text-right">%{total > 0 ? ((d.value / total) * 100).toFixed(1) : '0.0'}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Custom manual-branding rows from Report4 target plans ─────────── */

function buildCustomBrandingFromPlans(plansByYear, rangeStart, rangeEnd) {
  const start = new Date(rangeStart);
  const end = new Date(rangeEnd);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  const byKey = new Map();
  const excludeLabels = ['pinterest', 'medya satınalma', 'medya satinalma', 'tiktok', 'tik tok'];
  const addToBucket = (r, year, bucketKey, bucketLabel) => {
    if (!byKey.has(bucketKey)) byKey.set(bucketKey, { key: bucketKey, label: bucketLabel, harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0 });
    const bucket = byKey.get(bucketKey);
    const mv = r.metricsValues || {};
    for (let month = 1; month <= 12; month += 1) {
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
      const overlap = daysOverlap(start, end, monthStart, monthEnd);
      if (overlap <= 0) continue;
      const daysInMonth = new Date(year, month, 0).getDate();
      const ratio = overlap / daysInMonth;
      bucket.harcama += (Number(r.values?.[month] ?? r.values?.[String(month)] ?? 0) || 0) * ratio;
      const m = mv[month] ?? mv[String(month)] ?? {};
      bucket.gosterim += (Number(m.gosterim) || 0) * ratio;
      bucket.erisim   += (Number(m.erisim)   || 0) * ratio;
      bucket.izlenme  += (Number(m.izlenme)  || 0) * ratio;
      bucket.trafik   += (Number(m.trafik)   || 0) * ratio;
    }
  };
  plansByYear.forEach(({ year, rows }) => {
    (rows || []).forEach((r) => {
      if ((r.metricType || '').toLowerCase() !== 'spend') return;
      const rowKey = (r.key || '').toString();
      if (EXCLUDE_FROM_MANUAL_BRANDING.includes(rowKey)) return;
      const labelNorm = normalizeLabel(r.label);
      if (excludeLabels.some(l => labelNorm.includes(l))) return;
      // 🔴 Planlayıcıda satır BRANDING mi PERFORMANS mı, anahtarın önekinde duruyor
      // (`custom_branding_*` / `custom_performance_*`). Eskiden yalnız "elle
      // eklenmiş mi" diye bakılıyordu, o yüzden PERFORMANS satırları da branding
      // tablosuna giriyordu: Yataş Bedding'de Bing/Yandex/Cimri/Akakçe — Ağustos
      // 2026'da 168.022 ₺ performans harcaması branding sayılıyor, üstelik Yandex
      // ve Bing hem branding hem performans satırıyla İKİ KEZ listeleniyordu.
      // `bucket` sütunu bu satırlarda BOŞ, o yüzden ay bazlı eleme de yakalamıyor.
      if (rowKey.includes('_performance')) return;
      const isBrandingKey = rowKey.includes('_branding');
      const isCustom = r.isSystem === false;
      if (!isBrandingKey && !isCustom) return;
      const bucketKey = rowKey || `custom_${labelNorm.replace(/\s+/g, '_')}`;
      const bucketLabel = (r.label || bucketKey).trim() || 'Manuel';
      addToBucket(r, year, bucketKey, bucketLabel);
    });
  });
  return Array.from(byKey.values()).filter(x => x.harcama > 0 || x.gosterim > 0 || x.izlenme > 0 || x.trafik > 0);
}


/* ── Main Component ─────────────────────────────────────────────────── */

export default function CustomReport7() {
  const { startDate: initialStart, endDate: initialEnd } = getLastCompletedMonthRange();
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [pendingStartDate, setPendingStartDate] = useState(initialStart);
  const [pendingEndDate, setPendingEndDate] = useState(initialEnd);
  const [activePreset, setActivePreset] = useState('lastMonth');
  const [reportDataRaw, setReportData] = useState({});
  // Meta erişimi: DB toplamı yerine Meta API'den tekil değer (utils/metaReachDedup)
  const reachOf = useMetaReach(API, localStorage.getItem('currentBrandId'), startDate, endDate);
  const reportData = useMemo(() => withDedupReach(reportDataRaw, reachOf), [reportDataRaw, reachOf]);
  const [customRows, setCustomRows] = useState([]);
  const [sparklines, setSparklines] = useState({ harcama: [], gosterim: [], trafik: [], izlenme: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pdfRef = useRef(null);

  const applyPreset = useCallback((key) => {
    setActivePreset(key);
    if (key === 'custom') return;
    const range = resolvePreset(key);
    if (!range) return;
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setPendingStartDate(range.startDate);
    setPendingEndDate(range.endDate);
  }, []);

  const onDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingStartDate(s || null);
    setPendingEndDate(e || null);
    if (s && e) {
      setStartDate(s);
      setEndDate(e);
      setActivePreset('custom');
    }
  }, []);

  /* ── Fetchers ────────────────────────────────────────────────── */

  const fetchGoogleAds = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    const params = new URLSearchParams({ dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
    try {
      const [gRes, vRes] = await Promise.all([
        fetch(`${API}/api/google-ads/campaign-types-all?${params}`, { credentials: 'include', headers: hdr(brandId) }),
        fetch(`${API}/api/google-ads/video-performance-summary?${params}`, { credentials: 'include', headers: hdr(brandId) }),
      ]);
      let googleBuckets = {};
      let videoRows = [];
      if (gRes.ok) {
        const json = await gRes.json();
        googleBuckets = aggregateGoogleBranding(haricleriAyikla(json.rawData, brandId), isBrandingOnlyBrand(brandId));
      }
      if (vRes.ok) {
        const vJson = await vRes.json();
        videoRows = haricleriAyikla(Array.isArray(vJson) ? vJson : vJson.data || vJson.rawData || [], brandId);
        googleBuckets = overlayYouTubeVideoData(googleBuckets, videoRows, isBrandingOnlyBrand(brandId));
      }
      setReportData(prev => ({ ...prev, Google: googleBuckets }));
    } catch (e) { console.error('Google Ads branding fetch error:', e); }
  }, [startDate, endDate]);

  const fetchMeta = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    const params = new URLSearchParams({ dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
    try {
      const res = await fetch(`${API}/api/google-ads/unified-meta-ga4-performance?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const json = await res.json();
      const raw = haricleriAyikla(Array.isArray(json) ? json : json.data || json.rawData || [], brandId);
      setReportData(prev => ({ ...prev, Meta: aggregateMetaBranding(raw, isBrandingOnlyBrand(brandId)) }));
    } catch (e) { console.error('Meta branding fetch error:', e); }
  }, [startDate, endDate]);

  const fetchPinterest = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
    try {
      const res = await fetch(`${API}/api/pinterest/db/stats?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const json = await res.json();
      setReportData(prev => ({ ...prev, Pinterest: aggregatePinterestBranding(json) }));
    } catch (e) { console.error('Pinterest branding fetch error:', e); }
  }, [startDate, endDate]);

  const fetchMediaBuying = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    try {
      const periods = listYearMonthInRange(startDate, endDate);
      const responses = await Promise.all(
        periods.map(p =>
          fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${p.year}&month=${p.month}`, {
            credentials: 'include',
            headers: hdr(brandId),
          }).then(r => r.ok ? r.json() : null).catch(() => null)
        )
      );
      const mergedMedia = {}; // dinamik: aylara göre hangi mecra girildiyse birikir
      const mergedTiktok = { harcama:0, gosterim:0, erisim:0, izlenme:0, tiklama:0, trafik:0 };
      responses.forEach(json => {
        if (!json || !Array.isArray(json.sections)) return;
        const { mediaPlan, tiktok } = aggregateMediaBuyingBranding(json);
        Object.entries(mediaPlan).forEach(([ch, m]) => {
          if (!mergedMedia[ch]) mergedMedia[ch] = { harcama:0, gosterim:0, erisim:0, izlenme:0, tiklama:0, trafik:0 };
          Object.keys(m).forEach(k => { mergedMedia[ch][k] += m[k] || 0; });
        });
        Object.keys(tiktok).forEach(k => { mergedTiktok[k] += tiktok[k] || 0; });
      });
      setReportData(prev => ({
        ...prev,
        'Medya Planı': mergedMedia,
        'TikTok': { 'TikTok / Branding (Manuel)': mergedTiktok },
      }));
    } catch (e) { console.error('Media Buying fetch error:', e); }
  }, [startDate, endDate]);

  const fetchCustomBranding = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    try {
      const periods = listYearMonthInRange(startDate, endDate);
      const years = [...new Set(periods.map(p => p.year))];
      const responses = await Promise.all(
        years.map(year =>
          fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, {
            credentials: 'include',
            headers: hdr(brandId),
          }).then(r => r.ok ? r.json() : { rows: [] }).catch(() => ({ rows: [] }))
        )
      );
      const plansByYear = years.map((year, i) => ({ year, rows: responses[i]?.rows || [] }));
      setCustomRows(buildCustomBrandingFromPlans(plansByYear, startDate, endDate));
    } catch (e) { console.error('Custom branding fetch error:', e); }
  }, [startDate, endDate]);

  const fetchSparklines = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    const params = new URLSearchParams({ dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
    try {
      const [gRes, mRes] = await Promise.all([
        fetch(`${API}/api/google-ads/campaign-types-all?${params}`, { credentials: 'include', headers: hdr(brandId) }),
        fetch(`${API}/api/google-ads/unified-meta-ga4-performance?${params}`, { credentials: 'include', headers: hdr(brandId) }),
      ]);
      const gJson = gRes.ok ? await gRes.json() : null;
      const mJson = mRes.ok ? await mRes.json() : null;
      const brandingOnly = isBrandingOnlyBrand(brandId);
      const gRaw = haricleriAyikla(gJson?.rawData || [], brandId).filter(r => {
        if (brandingOnly) return true;
        const name = r.campaignName || r.campaign_name || '';
        const type = r.campaignType || r.campaign_type;
        return isBranding(name, type);
      });
      const mRaw = haricleriAyikla(Array.isArray(mJson) ? mJson : mJson?.data || mJson?.rawData || [], brandId).filter(r => {
        if (brandingOnly) return true;
        const name = r.campaign_name || r.campaignName || '';
        return isBranding(name, r.campaign_type || r.campaignType);
      });
      const dailyMap = new Map();
      const add = (date, field, value) => {
        const key = (date || '').substring(0, 10);
        if (!key) return;
        const cur = dailyMap.get(key) || { harcama: 0, gosterim: 0, trafik: 0, izlenme: 0 };
        cur[field] += Number(value) || 0;
        dailyMap.set(key, cur);
      };
      gRaw.forEach(r => {
        const d = r.date || r.day || r.reportDate;
        add(d, 'harcama',  r.googleAdsCost);
        add(d, 'gosterim', r.googleAdsImpression);
        add(d, 'trafik',   r.sessions);
      });
      mRaw.forEach(r => {
        const d = r.date || r.day || r.reportDate;
        add(d, 'harcama',  r.spend);
        add(d, 'gosterim', r.impressions);
        add(d, 'trafik',   r.ga4_sessions || r.ga4Sessions);
        add(d, 'izlenme',  r.view || r.trueplay || r.video_views || r.videoViews);
      });
      const sortedKeys = Array.from(dailyMap.keys()).sort();
      const series = { harcama: [], gosterim: [], trafik: [], izlenme: [] };
      sortedKeys.forEach(k => {
        const v = dailyMap.get(k);
        series.harcama.push(v.harcama);
        series.gosterim.push(v.gosterim);
        series.trafik.push(v.trafik);
        series.izlenme.push(v.izlenme);
      });
      setSparklines(series);
    } catch (e) { console.error('Sparklines fetch error:', e); }
  }, [startDate, endDate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchGoogleAds(),
      fetchMeta(),
      fetchPinterest(),
      fetchMediaBuying(),
      fetchCustomBranding(),
      fetchSparklines(),
    ]);
    setLoading(false);
  }, [fetchGoogleAds, fetchMeta, fetchPinterest, fetchMediaBuying, fetchCustomBranding, fetchSparklines]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Computed: tables ─────────────────────────────────────── */

  // 'Medya Planı' satırları artık girilen mecralara göre dinamik (statik Native/Display/Video değil).
  const mbChannelNames = useMemo(
    () => Object.keys(reportData['Medya Planı'] || {}),
    [reportData]
  );

  // Marka bazlı kural — her render'da okunur (marka değişince güncellenmesi için).
  const brandingOnly = isBrandingOnlyBrand(localStorage.getItem('currentBrandId'));

  const computedTable = useMemo(() => {
    const customSections = customRows.map(r => ({
      platform: r.label,
      rows: [{ name: `${r.label} / Branding` }],
    }));
    const base = BRANDING_TABLE.map(g => {
      if (g.platform === 'Medya Planı' && mbChannelNames.length) {
        return { ...g, rows: mbChannelNames.map(name => ({ name })) };
      }
      // Hepsi-branding markasında PMax dışı Google ve Meta Traffic/Conversion
      // harcaması da rapora girer — kendi satırları eklenir.
      if (brandingOnly && g.platform === 'Google') {
        return { ...g, rows: [...g.rows, { name: 'Search / Diğer' }] };
      }
      if (brandingOnly && g.platform === 'Meta') {
        return { ...g, rows: [...g.rows, { name: 'Traffic / Conversion' }] };
      }
      return g;
    });
    return [...base, ...customSections];
  }, [customRows, mbChannelNames, brandingOnly]);

  const computedSummary = useMemo(() => {
    const customSummary = customRows.map(r => ({
      label: r.label,
      sources: [{ platform: r.label, rows: [`${r.label} / Branding`] }],
    }));
    const base = SUMMARY_ROWS.map(sr => {
      if (sr.label === 'Medya Planı' && mbChannelNames.length) {
        return { ...sr, sources: [{ platform: 'Medya Planı', rows: mbChannelNames }] };
      }
      if (brandingOnly && sr.label === 'Google') {
        return { ...sr, sources: [{ platform: 'Google', rows: [...sr.sources[0].rows, 'Search / Diğer'] }] };
      }
      if (brandingOnly && sr.label === 'Meta') {
        return { ...sr, sources: [{ platform: 'Meta', rows: [...sr.sources[0].rows, 'Traffic / Conversion'] }] };
      }
      return sr;
    });
    return [...base, ...customSummary];
  }, [customRows, mbChannelNames, brandingOnly]);

  const effectiveReport = useMemo(() => {
    const data = { ...reportData };
    customRows.forEach(r => {
      data[r.label] = {
        [`${r.label} / Branding`]: {
          harcama: Math.round(r.harcama),
          gosterim: Math.round(r.gosterim),
          erisim: Math.round(r.erisim),
          izlenme: Math.round(r.izlenme),
          tiklama: Math.round(r.tiklama || 0),
          trafik: Math.round(r.trafik),
        },
      };
    });
    return data;
  }, [reportData, customRows]);

  const summaryData = useMemo(() => {
    const SUM_COLS = COLUMNS.filter(col => !col.compute); // derived columns computed at render time
    const rows = computedSummary.map(sr => {
      const items = [];
      sr.sources.forEach(src => {
        const pd = effectiveReport[src.platform] || {};
        src.rows.forEach(rowName => items.push(pd[rowName]));
      });
      // Erişim: Meta kovalarının kampanya kümeleri birleştirilip tekil sorulur
      const m = sumMetrics(items, SUM_COLS, reachOf);
      // Build per-campaign-type breakdown for inline pie (by spend)
      const breakdown = [];
      sr.sources.forEach(src => {
        const pd = effectiveReport[src.platform] || {};
        src.rows.forEach(rowName => {
          const v = pd[rowName]?.harcama || 0;
          if (v > 0) breakdown.push({ name: rowName, value: v });
        });
      });
      return { label: sr.label, metrics: m, breakdown };
    });
    const total = sumMetrics(rows.map(r => r.metrics), SUM_COLS, reachOf);
    return { rows, total };
  }, [effectiveReport, computedSummary, reachOf]);

  /* ── KPI allocation (top 4 channels by spend) ─────────────── */

  const kpiAllocation = useMemo(() => {
    const byChannel = summaryData.rows
      .map(r => ({ label: r.label, spend: r.metrics.harcama || 0 }))
      .filter(r => r.spend > 0)
      .sort((a, b) => b.spend - a.spend);
    const total = byChannel.reduce((s, r) => s + r.spend, 0);
    if (total === 0) return [];
    return byChannel.slice(0, 4).map(r => ({ label: r.label, pct: (r.spend / total) * 100 }));
  }, [summaryData]);

  /* ── Distribution pies (by channel) ────────────────────────── */

  const distByMetric = useMemo(() => {
    const build = (key) => summaryData.rows
      .map(r => ({ name: r.label, value: r.metrics[key] || 0 }))
      .filter(d => d.value > 0);
    return {
      harcama:  build('harcama'),
      trafik:   build('trafik'),
      izlenme:  build('izlenme'),
      gosterim: build('gosterim'),
    };
  }, [summaryData]);

  /* ── PDF Export ─────────────────────────────────────────── */

  const handleExportPDF = useCallback(async () => {
    if (exporting || !pdfRef.current) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1400, letterRendering: true, allowTaint: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * usableW) / canvas.width;
      let y = 0;
      let page = 0;
      while (y < imgH) {
        if (page > 0) pdf.addPage();
        const srcY = (y / imgH) * canvas.height;
        const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceH = (srcH * usableW) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgW, sliceH);
        y += usableH;
        page++;
      }
      pdf.save('Branding_Mecra_Kirilimli_Rapor.pdf');
    } catch (err) { console.error('PDF export failed:', err); }
    finally { setExporting(false); }
  }, [exporting]);

  /* ── Render ──────────────────────────────────────────────── */

  return (
    <div className="min-h-screen" ref={pdfRef}>
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 tracking-tight">Branding / Toplam</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">Mecra kırılımlı branding performans raporu</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ConsistencyBadge startDate={startDate} endDate={endDate} />
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <DatePicker
              selectsRange
              startDate={pendingStartDate}
              endDate={pendingEndDate}
              onChange={onDateRangeChange}
              dateFormat="dd MMM yyyy"
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[170px] cursor-pointer"
            />
          </div>
          <button onClick={fetchAll} disabled={loading} className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Yenile
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-md px-2.5 py-1 text-[11px] font-medium shadow-sm transition-colors"
          >
            {exporting ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
            {exporting ? 'PDF...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Date Presets */}
      <div className="mb-4 flex flex-wrap gap-1.5">
        {DATE_PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => applyPreset(p.key)}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
              activePreset === p.key
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Toplam Harcama"
          value={formatValue(summaryData.total.harcama || 0, 'currency')}
          trend={sparklines.harcama}
          color="#3B82F6"
          allocation={kpiAllocation}
        />
        <KpiCard
          label="Toplam Gösterim"
          value={formatShort(summaryData.total.gosterim || 0)}
          trend={sparklines.gosterim}
          color="#10B981"
        />
        <KpiCard
          label="Toplam Trafik"
          value={formatShort(summaryData.total.trafik || 0)}
          trend={sparklines.trafik}
          color="#F59E0B"
        />
        <KpiCard
          label="Toplam İzleme"
          value={formatShort(summaryData.total.izlenme || 0)}
          trend={sparklines.izlenme}
          color="#8B5CF6"
        />
      </div>

      {/* Summary Table — Mecra Kırılımları / Branding (with inline pies) */}
      <div className="mb-6">
        <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Mecra Kırılımları / Branding</h2>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[900px]">
              <thead>
                <tr>
                  <th className={S.headerCell} style={{ minWidth: 160 }}>Mecra</th>
                  <th className={S.headerCell} style={{ minWidth: 64 }}>Dağılım</th>
                  {COLUMNS.map(col => <th key={col.key} className={S.headerCell}>{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {summaryData.rows.map(row => (
                  <tr key={row.label}>
                    <td className={`${S.platformCell} text-[10.5px]`}>{row.label}</td>
                    <td className="border border-slate-200 bg-white p-1 text-center" style={{ width: 64 }}>
                      <div className="flex justify-center">
                        <InlinePie data={row.breakdown} size={42} />
                      </div>
                    </td>
                    {COLUMNS.map(col => (
                      <td key={col.key} className={S.dataCell}>{formatValue(getCellValue(row.metrics, col, { totalHarcama: summaryData.total.harcama || 0 }), col.format)}</td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className={S.totalLabel}>TOTAL</td>
                  <td className={`${S.totalCell}`}></td>
                  {COLUMNS.map(col => <td key={col.key} className={S.totalCell}>{formatValue(getCellValue(summaryData.total, col, { totalHarcama: summaryData.total.harcama || 0 }), col.format)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
          {loading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-30 rounded-lg">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-md shadow-md border border-slate-200">
                <Loader2 size={14} className="text-slate-500 animate-spin" />
                <span className="text-[11px] text-slate-500">Veriler yükleniyor…</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Table — Planlamadaki Kırılımlar / Branding */}
      <div className="mb-6">
        <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Planlamadaki Kırılımlar / Branding</h2>
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[1000px]">
              <thead>
                <tr>
                  <th className={S.headerCell} style={{ minWidth: 140 }}>Mecra</th>
                  <th className={S.headerCell} style={{ minWidth: 180 }}>Kampanya Türü / Objektif</th>
                  {COLUMNS.map(col => <th key={col.key} className={S.headerCell}>{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {computedTable.map(group => (
                  <React.Fragment key={group.platform}>
                    {group.rows.map((r, idx) => {
                      const pd = effectiveReport[group.platform] || {};
                      const m = pd[r.name] || {};
                      return (
                        <tr key={`${group.platform}-${r.name}`}>
                          {idx === 0 && (
                            <td className={S.platformCell} rowSpan={group.rows.length}>{group.platform}</td>
                          )}
                          <td className={S.rowLabel}>{r.name}</td>
                          {COLUMNS.map(col => (
                            <td key={col.key} className={S.dataCell}>{formatValue(getCellValue(m, col, { totalHarcama: summaryData.total.harcama || 0 }), col.format)}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                <tr>
                  <td className={S.totalLabel} colSpan={2}>TOTAL</td>
                  {COLUMNS.map(col => <td key={col.key} className={S.totalCell}>{formatValue(getCellValue(summaryData.total, col, { totalHarcama: summaryData.total.harcama || 0 }), col.format)}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Distribution Pies — one per metric */}
      <div className="mb-6">
        <h2 className="text-[13px] font-semibold text-slate-700 mb-2">Mecra Bazlı Dağılım</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <DistributionPie title="Harcama Dağılımı"  data={distByMetric.harcama}  format="currency" />
          <DistributionPie title="Trafik Dağılımı"   data={distByMetric.trafik}   format="number" />
          <DistributionPie title="İzleme Dağılımı"   data={distByMetric.izlenme}  format="number" />
          <DistributionPie title="Gösterim Dağılımı" data={distByMetric.gosterim} format="number" />
        </div>
      </div>
    </div>
  );
}
